<template>
  <section class="border border-gray-200 rounded-lg bg-white h-[500px] flex flex-col">
    <div class="px-5 py-4 border-b border-gray-200">
      <h2 class="text-base font-semibold text-gray-900">Long-running requests</h2>
      <p class="text-xs text-gray-600 mt-0.5">
        Latest 200 requests over the selected threshold; browser-observed duration.
      </p>
    </div>

    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <LoadingIndicator message="Loading long requests..." />
    </div>
    <div v-else-if="error" class="flex-1 overflow-auto p-5 text-sm text-red-dark">
      {{ error }}
    </div>
    <div v-else-if="!sortedRows.length" class="flex-1 p-5 text-sm text-gray-600">
      No requests exceeded this threshold.
    </div>
    <div v-else class="flex-1 min-h-0 overflow-auto">
      <table class="w-full text-[13px]" style="border-collapse: collapse">
        <colgroup>
          <col style="width: 14%" />
          <col style="width: 12%" />
          <col style="width: 17%" />
          <col style="width: 8%" />
          <col style="width: 28%" />
          <col style="width: 10%" />
          <col style="width: 11%" />
        </colgroup>
        <thead>
          <tr class="bg-gray-50 border-b border-gray-200 sticky top-0 z-[1]">
            <Latency_SortHeader
              v-for="(column, index) in columns"
              :key="column.key"
              :sort-key="column.key"
              :active-key="sortKey"
              :direction="sortDir"
              :th-class="headerClass(index)"
              @sort="sortBy"
            >{{ column.label }}</Latency_SortHeader>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in sortedRows" :key="`${row.occurred_at}:${index}`" class="border-b border-gray-100">
            <td class="pl-4 pr-2 py-2.5 align-middle text-gray-700 whitespace-nowrap">{{ formatDate(row.occurred_at) }}</td>
            <td class="sensitive px-2 py-2.5 align-middle text-gray-700">{{ row.tenant_slug || '—' }}</td>
            <td class="px-2 py-2.5 align-middle text-gray-700 break-words">{{ row.page_route || '—' }}</td>
            <td class="px-2 py-2.5 align-middle text-gray-700 font-medium">{{ row.method }}</td>
            <td class="px-2 py-2.5 align-middle font-mono text-xs text-gray-900 break-all">{{ row.endpoint }}</td>
            <td class="px-2 py-2.5 align-middle text-gray-900 font-semibold">{{ formatDuration(row.duration_ms) }}</td>
            <td class="pl-2 pr-4 py-2.5 align-middle" :class="outcomeClass(row.outcome)">
              {{ outcomeLabel(row) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import Latency_SortHeader from './Latency_SortHeader.vue';
import { compareRows, formatDuration } from './latency_format';

const COLUMNS = [
  { key: 'occurred_at', label: 'Time' },
  { key: 'tenant_slug', label: 'Tenant' },
  { key: 'page_route', label: 'Page' },
  { key: 'method', label: 'Method' },
  { key: 'endpoint', label: 'Endpoint' },
  { key: 'duration_ms', label: 'Duration' },
  { key: 'outcome', label: 'Outcome' },
];

const DATE_ACCESSORS = {
  occurred_at: (row) => new Date(row.occurred_at).getTime(),
};

export default {
  name: 'LatencyRequestTable',
  components: { Latency_SortHeader, LoadingIndicator },
  props: {
    rows: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
  },
  data() {
    return {
      columns: COLUMNS,
      sortKey: 'occurred_at',
      sortDir: 'desc',
    };
  },
  computed: {
    sortedRows() {
      return [...this.rows].sort(compareRows(this.sortKey, this.sortDir, DATE_ACCESSORS));
    },
  },
  methods: {
    formatDuration,
    sortBy(key) {
      if (this.sortKey !== key) {
        this.sortKey = key;
        this.sortDir = 'asc';
      } else {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      }
    },
    headerClass(index) {
      const edge = index === 0 ? 'pl-4' : index === this.columns.length - 1 ? 'pr-4' : '';
      return `px-2 py-2.5 text-left ${edge}`;
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    },
    outcomeLabel(row) {
      if (row.outcome === 'no_response') return 'No response';
      return row.status_code ? `HTTP ${row.status_code}` : 'Success';
    },
    outcomeClass(outcome) {
      if (outcome === 'server_error' || outcome === 'no_response') return 'text-red-dark font-medium';
      if (outcome === 'client_error') return 'text-gray-700';
      return 'text-green-dark';
    },
  },
};
</script>
